import {
  canAccessConsumerPage,
  canAccessDeveloperPage,
  canAccessMaintainerPage,
  hasValidRole,
} from '@/core/permissions/permission.core';
import { usePermissions } from '@/hooks/use-permissions/use-permissions.hook';
import { LoginPage } from '@/pages/login/login.page';
import { Suspense } from 'react';
import { Navigate, Outlet, Route, BrowserRouter as Router, Routes } from 'react-router';
// import { Loader } from '@/components/ui/loader/loader.component';
import * as LazyPages from './lazy-pages';
import { PreTailoringPage } from './lazy-pages';

const BadRoleRoute = () => {
  const { roles, isAuthenticated } = usePermissions();
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }
  if (!hasValidRole(roles)) {
    return <Outlet />;
  }
  return <Navigate to="/login" />;
};

const UnauthenticatedRoute = () => {
  const { roles, isAuthenticated } = usePermissions();
  if (!isAuthenticated) {
    return <Outlet />;
  }
  if (canAccessConsumerPage(roles)) {
    return <Navigate to="/products" />;
  }
  if (canAccessMaintainerPage(roles)) {
    return <Navigate to="/maintainer" />;
  }
  if (!hasValidRole(roles)) {
    return <Navigate to="/bad_role" />;
  }
  return <Outlet />;
};

const ConsumerRoute = () => {
  const { roles, isAuthenticated } = usePermissions();
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }
  if (canAccessConsumerPage(roles)) {
    return <Outlet />;
  }
  return <Navigate to="/login" />;
};

const DeveloperRoute = () => {
  const { roles, isAuthenticated } = usePermissions();
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }
  if (
    (import.meta.env.VITE_ENV === 'dev' || import.meta.env.VITE_ENV === 'qa') &&
    canAccessDeveloperPage(roles, import.meta.env.VITE_ENV)
  ) {
    return <Outlet />;
  }
  return <Navigate to="/login" />;
};

const MaintainerRoute = () => {
  const { roles, isAuthenticated } = usePermissions();
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }
  if (canAccessMaintainerPage(roles)) {
    return <Outlet />;
  }
  return <Navigate to="/login" />;
};

const ConsumerRoutes = () => <ConsumerRoute />;
const MaintainerRoutes = () => <MaintainerRoute />;
const DeveloperRoutes = () => <DeveloperRoute />;
const AuthRoutes = () => <UnauthenticatedRoute />;

// Loading fallback for Suspense boundary
// NOTE: Before lazy loading all pages loaded immediately so the loader is never displayed.
// The Loader component is commented out to maintain the same behavior as before.
// Uncomment <Loader /> if you want to show a spinner during lazy loading.
const LoadingFallback = () => <div className="flex items-center justify-center min-h-screen">{/* <Loader /> */}</div>;

export const RouterContainer = () => {
  return (
    <Router>
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          <Route element={<ConsumerRoutes />}>
            <Route path="/products" element={<LazyPages.ProductGroupsPage />} />
            <Route path="/products/:groupId" element={<LazyPages.ProductsPage />} />
            <Route path="/product/:id/pre-tailoring" element={<LazyPages.PreTailoringPage />} />
            <Route path="/product/:id/pre-tailoring" element={<PreTailoringPage />} />
            <Route path="/product/:id/final-tailoring" element={<LazyPages.TailoringPage />} />
            <Route path="/product/:id/summary" element={<LazyPages.SummaryPage />} />
            <Route path="/product/:id/export" element={<LazyPages.ExportPage />} />
            <Route path="/product/:id/data" element={<LazyPages.DataPage />} />
            <Route path="/templates" element={<LazyPages.TemplatesPage />} />
            <Route path="/template/:id/pre-tailoring" element={<LazyPages.TemplatesPretailoringPage />} />
            <Route path="/template/:id/final-tailoring" element={<LazyPages.TemplatesTailoringPage />} />
            <Route path="/update-product/:itemId" element={<LazyPages.UpdateSingleItemPage />} />
            <Route path="/update-template/:itemId" element={<LazyPages.UpdateSingleItemPage />} />
            <Route path="/update-all/:pmvId" element={<LazyPages.UpdateAllPage />} />
            <Route path="/substitutions" element={<LazyPages.ManageSubstitutionsPage />} />
          </Route>
          <Route element={<MaintainerRoutes />}>
            <Route path="/maintainer" element={<Navigate to="/maintainer/home/all" />} />
            <Route path="/maintainer/bulk-upload" element={<LazyPages.BulkUploadPage />} />
            <Route path="/maintainer/home" element={<Navigate to="/maintainer/home/all" />} />
            <Route path="/maintainer/home/all" element={<LazyPages.MaintainerHomePage />} />
            <Route path="/maintainer/home/do/:id" element={<LazyPages.MaintainerViewEditNodePage />} />
            <Route path="/maintainer/home/:node/:id" element={<LazyPages.MaintainerViewEditNodePage />} />
            <Route path="/maintainer/home/publish/do/:id" element={<LazyPages.MaintainerPublishNodePage />} />
          </Route>
          <Route element={<DeveloperRoutes />}>
            <Route path="/dev/test" element={<LazyPages.DevPage />} />
          </Route>
          <Route element={<AuthRoutes />}>
            <Route path="/redirect" element={<LazyPages.RedirectPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="*" element={<Navigate to="/login" />} />
          </Route>
          <Route element={<BadRoleRoute />}>
            <Route path="/bad_role" element={<LazyPages.InvalidRolePage />} />
          </Route>
        </Routes>
      </Suspense>
    </Router>
  );
};
